import React, { useState, useContext } from 'react'
import { useParams, Link } from 'react-router-dom'
import { graphql } from '@apollo/client/react/hoc'
import gql from "graphql-tag"
import { Container, Row, Col, Button } from 'reactstrap'
import './TaskManagementContainer.css'
import TaskManagementListHeader from './TaskManagementListHeader'
import TaskList from './TaskList'
import { AllBoardsQuery } from './Queries'
import withApolloClient from '../withApolloClient'
import { CurrentUserContext } from '../Context/CurrentUserContext'
import BoardActionsMenu from './TaskComponents/BoardActionsMenu'


const UpdateBoardMutation = gql`
    mutation UpdateBoardMutation($input: UpdateBoardMutationInput!) {
        updateBoard(input: $input) {
            ok
            errors {
                field
                messages
            }
        }
    }
`

function TaskManagementContainer({ allBoards, loadMoreEntries, apolloClient }) {
    const { boardId } = useParams()
    const { currentUser } = useContext(CurrentUserContext)
    const [orderBy, setOrderBy] = useState(['-date_added'])
    const [isEditingDescription, setIsEditingDescription] = useState(false)
    const [editedDescription, setEditedDescription] = useState("")
    const [showEditDescriptionIcon, setShowEditDescriptionIcon] = useState(false)
    const [isSaving, setIsSaving] = useState(false)


    const [filterValues, setFilterValues] = useState({ searchTerm: '', statuses: ['OPEN', 'IN PROGRESS'], isArchived: false })

    const boardDescription = allBoards?.edges?.find(board => board.node.id === boardId)?.node?.description || ""

    const updateOrderBy = (string) => {
        let order = [...orderBy]
        if (!order.includes(string) && !order.includes("-" + string)) {
            order = [string]
        } else {
            let index = order.indexOf(string)
            if (order.indexOf(string) > -1) {
                order[index] = "-" + string
            } else {
                index = order.indexOf("-" + string)
                index > -1 && (order[index] = string)
            }
        }
        setOrderBy(order)
    }
    const handleSaveTaskField = async (field, value) => {
        if (!field) return
        setIsSaving(true)

        const input = {
            boardId,
            [field]: value,
        }

        try {
            await apolloClient.mutate({
                mutation: UpdateBoardMutation,
                variables: { input },
                refetchQueries: ["Board"],
            })

            if (field === "description") setIsEditingDescription(false)
        } catch (error) {
            console.error(`Error saving ${field}:`, error)
        }
        finally {
            setIsSaving(false)
        }
    }
    return (
        <div className="TaskManagementContainer">
            <Container fluid>
                <TaskManagementListHeader activeTab={!boardId || boardId === 'tasks' ? "myTasks" : "boardTasks"} filterValues={filterValues} setFilterValues={(res) => setFilterValues({ ...res })} showNewBoardButton />
                <Row>
                    <Col xs="2" className="TaskBoardColumn">
                        <ul className="TaskBoardList">
                            <li className={!boardId || boardId === 'tasks' ? 'active' : ''}>
                                <Link to="/boards/tasks/">My Tasks</Link>
                            </li>
                            {allBoards?.edges?.map((board, idx) => (
                                <li className={boardId === board.node.id ? 'active' : ''} key={idx}>
                                    <Link to={`/boards/${board.node.id}/tasks/`}>{board.node.title}</Link>
                                </li>
                            ))}
                            {allBoards?.pageInfo?.hasNextPage && (
                                <li>
                                    <Button outline color="secondary" onClick={loadMoreEntries}>
                                        Load more...
                                    </Button>
                                </li>
                            )}
                        </ul>
                    </Col>
                    <Col xs="10" className="TaskListColumn">
                        <Row>
                            <Col xs="10">
                                <h1>{allBoards?.edges?.find(board => board.node.id === boardId)?.node?.title || "My Tasks"}</h1>
                                <div className="editable-title-container d-inline-flex align-items-start" onMouseEnter={() => setShowEditDescriptionIcon(true)} onMouseLeave={() => { setShowEditDescriptionIcon(false); if (isEditingDescription) handleSaveTaskField("description", editedDescription) }}>
                                    {!isEditingDescription ? (
                                        <>
                                            <p className="task-description mb-0">{boardDescription || ""}</p>

                                            {showEditDescriptionIcon && (
                                                <span className="edit-icon-box ml-2" onClick={() => { setEditedDescription(boardDescription || ""); setIsEditingDescription(true) }}><i className="fa fa-pencil-square-o"></i></span>
                                            )}
                                        </>
                                    ) : (
                                        <div className="d-flex align-items-center">
                                            <textarea className="form-control title-edit-input" style={{ width: "500px" }} value={editedDescription} autoFocus onChange={(e) => setEditedDescription(e.target.value)} onBlur={() => handleSaveTaskField("description", editedDescription)} onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) handleSaveTaskField("description", editedDescription) }} />
                                            {isSaving && <span className="ml-2"><i className="fa fa-spinner fa-spin"></i></span>}
                                        </div>
                                    )}
                                </div>
                            </Col>
                            <Col xs="2" className="text-right">
                                <BoardActionsMenu boardId={boardId} />
                            </Col>
                        </Row>
                        <hr />
                        <br />
                        {boardId ?
                            <TaskList boardId={boardId} {...filterValues} orderBy={orderBy} updateOrderBy={updateOrderBy} /> :
                            <TaskList assignedToIds={[currentUser.id]} {...filterValues} orderBy={orderBy} updateOrderBy={updateOrderBy} />
                        }
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default graphql(AllBoardsQuery, {
    options: ({ searchTerm }) => ({
        variables: {
            searchTerm,
            orderBy: ["date_added"],
            isArchived: false,
            first: 10
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network'
    }),
    props: ({ data: { loading, allBoards, fetchMore, searchTerm, variables, refetch } }) => (
        {
            loading,
            allBoards,
            variables,
            refetch,
            refetchDriversQuery: () => {
                //No apollo function that refetches in place with pagination considered so this function instead
                let currentLength = allBoards && allBoards.edges ? allBoards.edges.length : 1
                return fetchMore({
                    query: AllBoardsQuery,
                    variables: {
                        ...variables,
                        first: currentLength,
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        const newEdges = fetchMoreResult.allBoards.edges
                        const pageInfo = fetchMoreResult.allBoards.pageInfo
                        return {
                            allBoards: {
                                edges: [...newEdges],
                                pageInfo,
                                searchTerm: previousResult.searchTerm,
                                __typename: previousResult.allBoards.__typename
                            },
                        }
                    },
                })
            },
            loadMoreEntries: () => {
                return fetchMore({
                    query: AllBoardsQuery,
                    variables: {
                        cursor: allBoards.pageInfo.endCursor,
                        ...variables
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        const newEdges = fetchMoreResult.allBoards.edges
                        const pageInfo = fetchMoreResult.allBoards.pageInfo

                        return {
                            // Put the new comments at the end of the list and update `pageInfo`
                            // so we have the new `endCursor` and `hasNextPage` values
                            allBoards: {
                                edges: [...previousResult.allBoards.edges, ...newEdges],
                                pageInfo,
                                searchTerm: previousResult.searchTerm,
                                __typename: previousResult.allBoards.__typename
                            },
                        }
                    },
                })
            },
        }
    )
})(withApolloClient(TaskManagementContainer))